import React, { useState, useRef, useEffect } from 'react';
import { Input } from './input';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './command';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '../../lib/utils';

// Country data with dial codes and flags
const countries = [
  { code: 'PK', name: 'Pakistan', dial: '+92', flag: '🇵🇰' },
  { code: 'IN', name: 'India', dial: '+91', flag: '🇮🇳' },
  { code: 'BD', name: 'Bangladesh', dial: '+880', flag: '🇧🇩' },
  { code: 'NP', name: 'Nepal', dial: '+977', flag: '🇳🇵' },
  { code: 'LK', name: 'Sri Lanka', dial: '+94', flag: '🇱🇰' },
  { code: 'NG', name: 'Nigeria', dial: '+234', flag: '🇳🇬' },
  { code: 'GH', name: 'Ghana', dial: '+233', flag: '🇬🇭' },
  { code: 'KE', name: 'Kenya', dial: '+254', flag: '🇰🇪' },
  { code: 'ZA', name: 'South Africa', dial: '+27', flag: '🇿🇦' },
  { code: 'EG', name: 'Egypt', dial: '+20', flag: '🇪🇬' },
  { code: 'US', name: 'United States', dial: '+1', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', dial: '+44', flag: '🇬🇧' },
  { code: 'CA', name: 'Canada', dial: '+1', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', dial: '+61', flag: '🇦🇺' },
  { code: 'NZ', name: 'New Zealand', dial: '+64', flag: '🇳🇿' },
  { code: 'DE', name: 'Germany', dial: '+49', flag: '🇩🇪' },
  { code: 'FR', name: 'France', dial: '+33', flag: '🇫🇷' },
  { code: 'IT', name: 'Italy', dial: '+39', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', dial: '+34', flag: '🇪🇸' },
  { code: 'NL', name: 'Netherlands', dial: '+31', flag: '🇳🇱' },
  { code: 'BE', name: 'Belgium', dial: '+32', flag: '🇧🇪' },
  { code: 'SE', name: 'Sweden', dial: '+46', flag: '🇸🇪' },
  { code: 'NO', name: 'Norway', dial: '+47', flag: '🇳🇴' },
  { code: 'DK', name: 'Denmark', dial: '+45', flag: '🇩🇰' },
  { code: 'FI', name: 'Finland', dial: '+358', flag: '🇫🇮' },
  { code: 'IE', name: 'Ireland', dial: '+353', flag: '🇮🇪' },
  { code: 'CH', name: 'Switzerland', dial: '+41', flag: '🇨🇭' },
  { code: 'AT', name: 'Austria', dial: '+43', flag: '🇦🇹' },
  { code: 'PL', name: 'Poland', dial: '+48', flag: '🇵🇱' },
  { code: 'RU', name: 'Russia', dial: '+7', flag: '🇷🇺' },
  { code: 'UA', name: 'Ukraine', dial: '+380', flag: '🇺🇦' },
  { code: 'TR', name: 'Turkey', dial: '+90', flag: '🇹🇷' },
  { code: 'SA', name: 'Saudi Arabia', dial: '+966', flag: '🇸🇦' },
  { code: 'AE', name: 'UAE', dial: '+971', flag: '🇦🇪' },
  { code: 'QA', name: 'Qatar', dial: '+974', flag: '🇶🇦' },
  { code: 'KW', name: 'Kuwait', dial: '+965', flag: '🇰🇼' },
  { code: 'OM', name: 'Oman', dial: '+968', flag: '🇴🇲' },
  { code: 'BH', name: 'Bahrain', dial: '+973', flag: '🇧🇭' },
  { code: 'JO', name: 'Jordan', dial: '+962', flag: '🇯🇴' },
  { code: 'CN', name: 'China', dial: '+86', flag: '🇨🇳' },
  { code: 'JP', name: 'Japan', dial: '+81', flag: '🇯🇵' },
  { code: 'KR', name: 'South Korea', dial: '+82', flag: '🇰🇷' },
  { code: 'SG', name: 'Singapore', dial: '+65', flag: '🇸🇬' },
  { code: 'MY', name: 'Malaysia', dial: '+60', flag: '🇲🇾' },
  { code: 'ID', name: 'Indonesia', dial: '+62', flag: '🇮🇩' },
  { code: 'TH', name: 'Thailand', dial: '+66', flag: '🇹🇭' },
  { code: 'VN', name: 'Vietnam', dial: '+84', flag: '🇻🇳' },
  { code: 'PH', name: 'Philippines', dial: '+63', flag: '🇵🇭' },
  { code: 'BR', name: 'Brazil', dial: '+55', flag: '🇧🇷' },
  { code: 'MX', name: 'Mexico', dial: '+52', flag: '🇲🇽' },
  { code: 'AR', name: 'Argentina', dial: '+54', flag: '🇦🇷' },
  { code: 'CO', name: 'Colombia', dial: '+57', flag: '🇨🇴' },
  { code: 'CL', name: 'Chile', dial: '+56', flag: '🇨🇱' },
  { code: 'IR', name: 'Iran', dial: '+98', flag: '🇮🇷' },
  { code: 'IQ', name: 'Iraq', dial: '+964', flag: '🇮🇶' },
  { code: 'AF', name: 'Afghanistan', dial: '+93', flag: '🇦🇫' },
  { code: 'ET', name: 'Ethiopia', dial: '+251', flag: '🇪🇹' },
  { code: 'MA', name: 'Morocco', dial: '+212', flag: '🇲🇦' },
];

// Parse phone number to extract country code and number
const parsePhone = (phone, nationality = null) => {
  if (!phone) {
    // If nationality is provided, use that country's code as default
    if (nationality) {
      const nationalityLower = nationality.toLowerCase();
      const matchedCountry = countries.find(c => 
        c.name.toLowerCase() === nationalityLower ||
        c.name.toLowerCase().includes(nationalityLower) ||
        nationalityLower.includes(c.name.toLowerCase())
      );
      if (matchedCountry) {
        return { countryCode: matchedCountry.code, number: '' };
      }
    }
    return { countryCode: 'PK', number: '' };
  }
  
  const cleaned = phone.replace(/\s+/g, '');
  
  // Sort countries by dial code length (longest first) to match more specific codes first
  const sortedCountries = [...countries].sort((a, b) => b.dial.length - a.dial.length);
  
  // Try to find matching country by dial code
  for (const country of sortedCountries) {
    if (cleaned.startsWith(country.dial)) {
      return {
        countryCode: country.code,
        number: cleaned.slice(country.dial.length)
      };
    }
  }
  
  // If starts with +, try to extract dial code with different lengths
  if (cleaned.startsWith('+')) {
    // Try different dial code lengths (4, 3, 2, 1 digits)
    for (let len = 4; len >= 1; len--) {
      const dialMatch = cleaned.match(new RegExp(`^\\+(\\d{${len}})`));
      if (dialMatch) {
        const dial = '+' + dialMatch[1];
        const country = sortedCountries.find(c => c.dial === dial);
        if (country) {
          return {
            countryCode: country.code,
            number: cleaned.slice(dial.length)
          };
        }
      }
    }
  }
  
  // If nationality is provided and no dial code found, use nationality's country code
  if (nationality) {
    const nationalityLower = nationality.toLowerCase();
    const matchedCountry = countries.find(c => 
      c.name.toLowerCase() === nationalityLower ||
      c.name.toLowerCase().includes(nationalityLower) ||
      nationalityLower.includes(c.name.toLowerCase())
    );
    if (matchedCountry) {
      return { countryCode: matchedCountry.code, number: cleaned.replace(/^\+?/, '') };
    }
  }
  
  // Default to first country (Pakistan) if no match
  return { countryCode: 'PK', number: cleaned.replace(/^\+?/, '') };
};

export const PhoneInput = ({ 
  value, 
  onChange, 
  placeholder = "Phone number",
  disabled = false,
  className,
  nationality,
  'data-testid': dataTestId
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  
  // Parse initial value with nationality hint
  const parsed = parsePhone(value, nationality);
  const [selectedCountry, setSelectedCountry] = useState(
    countries.find(c => c.code === parsed.countryCode) || countries[0]
  );
  const [phoneNumber, setPhoneNumber] = useState(parsed.number);

  // Update when value or nationality changes externally
  useEffect(() => {
    const parsed = parsePhone(value, nationality);
    const country = countries.find(c => c.code === parsed.countryCode);
    if (country) setSelectedCountry(country);
    setPhoneNumber(parsed.number);
  }, [value, nationality]);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setOpen(false);
    // Update the full phone number
    const fullNumber = country.dial + phoneNumber;
    onChange?.(fullNumber);
  };

  const handlePhoneChange = (e) => {
    const newNumber = e.target.value.replace(/[^\d]/g, '');
    setPhoneNumber(newNumber);
    const fullNumber = selectedCountry.dial + newNumber;
    onChange?.(fullNumber);
  };

  // Filter countries based on search
  const filteredCountries = search
    ? countries.filter(c => 
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.dial.includes(search) ||
        c.code.toLowerCase().includes(search.toLowerCase())
      )
    : countries;

  return (
    <div className={cn("flex", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-[110px] justify-between rounded-r-none border-r-0 px-2"
            data-testid={dataTestId ? `${dataTestId}-country` : undefined}
          >
            <span className="flex items-center gap-1 text-sm">
              <span className="text-lg">{selectedCountry.flag}</span>
              <span className="text-xs text-muted-foreground">{selectedCountry.dial}</span>
            </span>
            <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          <Command>
            <CommandInput 
              placeholder="Search country..." 
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup className="max-h-[250px] overflow-y-auto">
                {filteredCountries.map((country) => (
                  <CommandItem
                    key={country.code}
                    value={country.name}
                    onSelect={() => handleCountrySelect(country)}
                    className="cursor-pointer"
                  >
                    <span className="text-lg mr-2">{country.flag}</span>
                    <span className="flex-1">{country.name}</span>
                    <span className="text-muted-foreground text-sm">{country.dial}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Input
        type="tel"
        value={phoneNumber}
        onChange={handlePhoneChange}
        placeholder={placeholder}
        disabled={disabled}
        className="rounded-l-none flex-1"
        data-testid={dataTestId}
      />
    </div>
  );
};

export default PhoneInput;
